import { useEffect } from 'react';

/**
 * Keeps the screen awake while `active` — a tablet that dims mid-cycle hides the emergency stop.
 * The Android app does this natively; this covers the browser. Wake locks are released when the
 * page is hidden, so it is re-acquired whenever the page becomes visible again.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) await lock.release();
        else sentinel = lock;
      } catch {
        // Denied (battery saver, unsupported context): the cycle still runs, the screen may dim.
      }
    };
    const onVisibility = () => void acquire();

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => undefined);
    };
  }, [active]);
}
